import type { AxiosInstance } from 'axios';
import { IApiLastUpdateResponse } from 'interfaces/schedule/IApiLastUpdateResponse';
import { ILatestSync } from 'interfaces/schedule/ILatestSync';
import { IRosterResponse } from 'interfaces/team/IRosterResponse';
import { ITeam } from 'interfaces/team/ITeam';
import { injectable } from 'inversify';
import moment, { Duration } from 'moment';
import { Result } from 'services/Result';
import { NotificationStore } from 'stores/NotificationStore';
import { OfflineError } from 'types/OfflineError';
import { IApiGameLevel, IApiLeague, IOrganization } from '../../interfaces/team/IApiScheduleResponse';

@injectable()
export class ContextualService {
  private axiosInstance: AxiosInstance;
  private notificationStore: NotificationStore;

  constructor(axiosInstance: AxiosInstance, notificationStore: NotificationStore) {
    this.axiosInstance = axiosInstance;
    this.notificationStore = notificationStore;
  }

  GetAllTeams = async () => {
    const response = await this.axiosInstance.get<Result<ITeam[]>>('context/teams');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error(`Failed to fetch teams: ${response.status}`);
  };

  GetTeamRoster = async (teamId: string) => {
    const response = await this.axiosInstance.get<Result<IRosterResponse>>(`context/rosters/${teamId}`);
    if (response.data && !response.data.isError && response.data.data && response.data.data.players) {
      return response.data.data;
    }
    if (response.status === 404) {
      throw new OfflineError('Failed to fetch roster: Offline');
    }
    throw Error(`Failed to fetch roster: ${response.status}`);
  };

  DisableScheduleAutoStart = async (scheduleId: string) => {
    const response = await this.axiosInstance.post('gameschedule/ChangeScheduleState', { id: scheduleId, stateIssued: 'WillNotBeIssued' });
    if (response.status !== 200) {
      throw Error(`Failed to update schedule: ${response.status}`);
    }
  };

  GetGameLevels = async () => {
    const response = await this.axiosInstance.get<Result<IApiGameLevel[]>>('context/gamelevels');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error(`Failed to fetch gameLevels: ${response.status}`);
  };

  GetLeagues = async () => {
    const response = await this.axiosInstance.get<Result<IApiLeague[]>>('context/leagues');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error(`Failed to fetch leagues: ${response.status}`);
  };

  GetOwningOrganizations = async (fieldId: string) => {
    const response = await this.axiosInstance.get<Result<IOrganization[]>>('context/organization/');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error(`Failed to fetch owning organizations: ${response.status}`);
  };

  SyncSchedulesAndRosters = async () => {
    const response = await this.axiosInstance.get<Result<boolean>>('synchronization/trigger/schedulesandrosters');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('Failed to sync schedules');
  };

  SyncLeagues = async () => {
    const response = await this.axiosInstance.get<Result<boolean>>('synchronization/trigger/leagues');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('Failed to sync leagues');
  };

  SyncLevels = async () => {
    const response = await this.axiosInstance.get<Result<boolean>>('synchronization/trigger/levels');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('Failed to sync levels');
  };

  SyncTeams = async () => {
    const response = await this.axiosInstance.get<Result<boolean>>('synchronization/trigger/teams');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('Failed to sync teams');
  };

  SyncOwningOrgs = async () => {
    const response = await this.axiosInstance.get<Result<boolean>>('synchronization/trigger/organizations');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('Failed to sync organizations');
  };

  GetLastSchedulesUpdate = async () => {
    const response = await this.axiosInstance.get<Result<IApiLastUpdateResponse>>('synchronization/schedules/lastupdate');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('Failed to get last update of schedules');
  };

  GetLastLeaguesUpdate = async () => {
    const response = await this.axiosInstance.get<Result<IApiLastUpdateResponse>>('synchronization/leagues/lastupdate');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('Failed to get last update of leagues');
  };

  GetLastLevelsUpdate = async () => {
    const response = await this.axiosInstance.get<Result<IApiLastUpdateResponse>>('synchronization/levels/lastupdate');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('Failed to get last update of levels');
  };

  GetLastRostersUpdate = async () => {
    const response = await this.axiosInstance.get<Result<IApiLastUpdateResponse>>('synchronization/rosters/lastupdate');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('Failed to get last update of rosters');
  };

  GetLastTeamsUpdate = async () => {
    const response = await this.axiosInstance.get<Result<IApiLastUpdateResponse>>('synchronization/teams/lastupdate');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('Failed to get last update of teams');
  };

  SyncAll = async () => {
    const schedules = this.SyncSchedulesAndRosters();
    const leagues = this.SyncLeagues();
    const levels = this.SyncLevels();
    const teams = this.SyncTeams();
    const orgs = this.SyncOwningOrgs();
    await Promise.all([schedules, leagues, levels, teams, orgs]);
  };

  GetLatestSyncAll = async () => {
    const schedulesUpdate = this.GetLastSchedulesUpdate();
    const leaguesUpdate = this.GetLastLeaguesUpdate();
    const levelsUpdate = this.GetLastLevelsUpdate();
    const rostersUpdate = this.GetLastRostersUpdate();
    const teamsUpdate = this.GetLastTeamsUpdate();

    try {
      const dates: IApiLastUpdateResponse[] = await Promise.all([schedulesUpdate, leaguesUpdate, levelsUpdate, rostersUpdate, teamsUpdate]);

      const moments: any[] = [];
      for (let i = 0; i < dates.length; i++) {
        if (dates[i]) {
          moments.push(moment(dates[i].updated));
        }
      }

      const lastupdate: Duration = moment.duration(moment.min(moments).diff(Date.now()));

      return {
        days: lastupdate.days() * -1,
        hours: lastupdate.hours() * -1,
        minutes: lastupdate.minutes() * -1,
      } as ILatestSync;
    } catch (err: unknown) {
      if (err instanceof Error) {
        const uniqueId = `contextual-service-sync-error-${Date.now()}`;
        this.notificationStore.addNotification({
          id: uniqueId,
          message: err.message,
          variant: 'error',
          removable: true,
          onClose: () => {
            this.notificationStore.removeNotification(uniqueId);
          },
        });
      }
    }
  };

  GetUmpires = async (): Promise<IUmpire[]> => {
    const response = await this.axiosInstance.get<Result<IUmpire[]>>('context/Umpires/');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error(`Failed to fetch umpires: ${response.status}`);
  };
}
